import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Motion";
import { LINKS } from "../lib/content";

const SECTIONS = [
  {
    label: "Problem",
    body: [
      "Enterprise sales teams were reinventing ROI math on every deal. Each opportunity produced a new spreadsheet, a new set of assumptions, and a new definition of \"value\" - none of which survived contact with a buyer's procurement and finance teams. Discount rates were inconsistent, cost baselines were undocumented, and benefit claims traced back to gut feel rather than measured data.",
      "The commercial consequences were real: deal cycles stretched as CFO offices asked questions the models couldn't answer, estimates swung wildly between reps, and credibility eroded exactly at the moment it mattered most - when a champion had to defend the investment internally. The organization needed a standardized, defensible way to quantify solution value that any consultant or seller could pick up and trust.",
    ],
  },
  {
    label: "Approach",
    body: [
      "I designed a standardized value-engineering toolkit built on the three numbers finance teams actually use: TCO, NPV, and IRR. Every assumption became a named, documented parameter - loaded labor rates, adoption curves, usage-based API costs at expected and pessimistic volumes, maintenance budgeted at 15-20% of build cost annually - instead of a number buried in a cell.",
      "The framework layered in parameterized industry benchmarking, so estimates started from defensible reference points rather than blank pages, and sensitivity analysis, so buyers could see exactly how the business case behaved when key assumptions moved. Governance controls locked the calculation logic while leaving inputs adjustable, and the entire structure was designed for MCP-ready workflows - enabling natural-language business querying over the model itself.",
    ],
  },
  {
    label: "Outcome",
    body: [
      "The toolkit became a reusable value-engineering asset deployed across multiple enterprise engagements. Recalculation errors dropped by 40%, because the parameterized structure eliminated the ad-hoc spreadsheet edits that had been quietly corrupting estimates. Estimate accuracy improved by roughly 20%, measured against realized outcomes on closed engagements.",
      "Most importantly, the numbers started surviving scrutiny. Average deal size rose by about 10% - not because the figures got bigger, but because they became defensible in front of the people who sign: CFOs, procurement leads, and boards. What used to be a bespoke exercise on every deal is now a governed, repeatable capability.",
    ],
  },
];

export default function RoiToolkit() {
  useEffect(() => { document.title = "Enterprise ROI Modeling & Value Engineering Toolkit - Yash Bhatt"; }, []);
  return (
    <main data-testid="roi-toolkit-page" className="pt-16">
      <header className="relative overflow-hidden border-b border-border">
        <div className="mesh-glow w-[500px] h-[500px] bg-violet-600/15 -top-40 -right-32" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <li><Link to="/" className="hover:text-secondary transition-colors duration-200">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/projects" className="hover:text-secondary transition-colors duration-200">Projects</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-secondary">ROI Toolkit</li>
            </ol>
          </nav>
          <MaskedLine>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-secondary mb-5">AI Strategy · Value Engineering</p>
          </MaskedLine>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.15] pb-2">
            <MaskedLine delay={0.15}>Enterprise ROI Modeling & <span className="text-gradient">Value Engineering Toolkit</span></MaskedLine>
          </h1>
          <MaskedLine delay={0.3}>
            <p className="mt-6 font-display text-lg text-gradient font-medium">+20% estimate accuracy · +10% average deal size · 40% fewer recalculation errors</p>
          </MaskedLine>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        {SECTIONS.map((s, si) => (
          <Reveal key={s.label} delay={si * 0.05} className="mb-14">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-5">{s.label}</p>
            {s.body.map((para, pi) => (
              <p key={pi} className="text-base md:text-lg text-foreground/85 leading-relaxed mb-5">{para}</p>
            ))}
          </Reveal>
        ))}

        <Reveal className="my-16">
          <figure data-testid="roi-toolkit-summary">
            <div className="rounded-2xl border border-border bg-gradient-to-br from-navy-950 via-card to-navy-950 overflow-hidden">
              <div className="border-b border-border/60 px-6 md:px-8 py-5 flex items-center justify-between flex-wrap gap-3 bg-[color:var(--card)]/60">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-secondary">ROI Summary · Enterprise AI ROI Model</p>
                  <h3 className="mt-1 font-display text-lg md:text-xl font-medium tracking-tight">Base case · 12% discount · 100% realization</h3>
                </div>
                <span className="font-mono text-[10px] text-muted-foreground px-2.5 py-1 rounded-full border border-border">5-yr horizon</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-y md:divide-y-0 divide-border/60">
                {[
                  { l: "NPV (5-yr)", v: "$593,310", accent: true },
                  { l: "IRR", v: "76.0%" },
                  { l: "Payback", v: "2.10 yrs" },
                  { l: "5-yr ROI", v: "176.1%" },
                  { l: "Benefit-Cost", v: "1.76×" },
                ].map((m) => (
                  <div key={m.l} className="px-5 py-6 md:py-7">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{m.l}</p>
                    <p className={`mt-2 font-display text-2xl md:text-3xl font-medium tracking-tight ${m.accent ? "text-gradient" : ""}`}>{m.v}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-border/60 px-6 md:px-8 py-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Cash-flow trajectory · $ thousands</p>
                <div className="grid grid-cols-6 gap-2 md:gap-3">
                  {[
                    { y: "Y0", val: -350, cum: -350 },
                    { y: "Y1", val: 77, cum: -273 },
                    { y: "Y2", val: 238, cum: -35 },
                    { y: "Y3", val: 352, cum: 317 },
                    { y: "Y4", val: 358, cum: 675 },
                    { y: "Y5", val: 365, cum: 1039 },
                  ].map((c) => (
                    <div key={c.y} className="rounded-lg bg-background/40 border border-border/50 px-2 py-3 text-center">
                      <p className="font-mono text-[9px] text-secondary">{c.y}</p>
                      <p className={`mt-1 text-xs font-medium ${c.val < 0 ? "text-red-400" : "text-foreground"}`}>{c.val < 0 ? `(${Math.abs(c.val)})` : `+${c.val}`}</p>
                      <p className={`text-[10px] font-mono mt-0.5 ${c.cum < 0 ? "text-muted-foreground" : "text-blue-400"}`}>cum {c.cum >= 0 ? "+" : ""}{c.cum}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Payback achieved in Year 3 · cumulative crosses zero</p>
              </div>
            </div>
            <figcaption className="mt-4 text-center text-xs font-mono text-muted-foreground">
              The parameterized ROI model — TCO, NPV, IRR with sensitivity analysis. Downloadable in the <Link to="/resources" className="text-secondary hover:underline">Knowledge Library</Link>.
            </figcaption>
          </figure>
        </Reveal>

        <Reveal className="mt-12 flex flex-wrap gap-2">
          {["Python", "ROI Modeling", "MCP", "Sensitivity Analysis", "Benchmarking"].map((t) => (
            <span key={t} className="px-3 py-1 rounded-full border border-border text-xs text-muted-foreground">{t}</span>
          ))}
        </Reveal>

        <Reveal className="mt-16 pt-10 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <Link to="/projects" data-testid="roi-toolkit-back-link" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline">
            <ArrowLeft size={14} /> All case studies
          </Link>
          <a
            href={LINKS.calendly} target="_blank" rel="noreferrer"
            data-testid="roi-toolkit-cta"
            className="group inline-flex h-12 items-center gap-2 px-7 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-200"
          >
            Book a Consultation <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </article>
    </main>
  );
}
